import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPage, updatePage } from "../../../api/userApi";
import Button from '@mui/material/Button';
import {SaveRounded, PostAddRounded} from "@mui/icons-material";
import {Form, Input, Switch, Select, notification, Drawer, Tag, Row, Col, Divider, Typography, Image} from 'antd';
import { selectFullPortfolios } from "../../../store/slices/portfolios.slice";
import Files from "../../Files/Files";
import UserDataSelector from "../../../SuperComponents/UserDataSelector";
import PortfolioSelectorV2 from "../PortfolioSelectors/PortfolioSelectorV2";
import AppSelector from "../../../components/AppSelector";
import {allApps} from "../../../configs/AppsConfig";
import {GoogleCloudStorageBucketUrl} from "../../../configs/Domain";


const ALLOWED_IQ_APPS = ['screener', 'positions', 'resources', 'smalab']

const PortfolioPageForm = ({ initialValues, pageId, toggleBack }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const portfolios = useSelector((state) => selectFullPortfolios(state, initialValues?.portfolio_ids));
  const userDefaults = useSelector((state) => state.user?.data?.defaultFields?.pages);

  const [options, setOptions] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPortfolios, setSelectedPortfolios] = useState(portfolios);
  const [selectedTouchpoints, setSelectedTouchpoints] = useState(initialValues?.touchpoint_ids || []);
  const [selectedFiles, setSelectedFiles] = useState(initialValues?.files || []);
  const [emails, setEmails] = useState(initialValues?.emails || []);
  const [apps, setApps] = useState(initialValues?.apps || []);
  const [brand, setBrand] = useState(initialValues?.brand || '');


 const handleSelectedFiles = (newFiles) => {
    if (Array.isArray(newFiles) && newFiles.length > 0) {
      const uniqueFiles = newFiles.filter(file => !selectedFiles.includes(file));
      if(uniqueFiles.length > 0){
        setSelectedFiles([...selectedFiles, ...uniqueFiles]);
        setDrawerVisible(false);
      } else {
        notification.warning({
          message: 'File already added',
        });
      }
    }
  };

  const handleRemoveFile = (filePath) => {
    setSelectedFiles(selectedFiles.filter(file => file !== filePath));
  };

  useEffect(() => {
    if (portfolios) {
      const newOptions = portfolios.map(portfolio => ({
        label: portfolio.name,
        value: portfolio.id
      }));
      setOptions(newOptions);
    }
  }, [initialValues?.portfolio_ids]);

  useEffect(()=>{
      form.setFieldsValue({
          touchpoint_ids: selectedTouchpoints,
          portfolio_ids: selectedPortfolios.map(portfolio => portfolio.id),
          emails,
          files: selectedFiles,
          apps,
          brand
      });
  }, [selectedTouchpoints, selectedPortfolios, emails, selectedFiles, apps, brand]);

  const handleSubmit = async (values) => {

    try {
        if (values.is_public === false && emails.length === 0) {
              notification.warning({
                message: 'Validation Error',
                description: 'Private pages must have at least one email associated. Please add an email to continue.',
              });
              return;
        }
        const formData = {
          ...values,
          files: selectedFiles,
            apps,
            brand,
        }
        if (pageId) {
            // Update existing page
            await dispatch(updatePage({ pageId: pageId, data: formData })).then(()=>{
                toggleBack?.();
            })
            notification.success({
              message: 'Page updated successfully',
            });
          } else {
            // Create new page
            await dispatch(createPage({data: formData})).then(()=>{
                toggleBack?.()
            })
            notification.success({
              message: 'Page created successfully',
            });
          }
        } catch (error) {
          notification.error({
            message: 'Error',
            description: error.message,
          });
        }
  };


  return (
    <>
      <Form
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
          layout="vertical"
      >
        <Row justify={'start'} gutter={96}>
            <Col xs={20} xl={10}>
                <Form.Item className={'px-5'}>
                    <Button variant={'contained'} startIcon={<SaveRounded/>} color={'info'} size={'large'} type={'submit'}>
                      Save IQ Page
                    </Button>
                </Form.Item>
            </Col>
        </Row>
        <Row justify={'center'} gutter={96}>
            <Col xs={20} xl={10}>
                {brand && <Image src={`${GoogleCloudStorageBucketUrl}${brand}`} height={48} width={'auto'} />}
                <Form.Item
                  name="title"
                  className={'border-sm round-2 p-3'}
                  label={<Typography.Title level={5}>IQ Title</Typography.Title>}
                  rules={[{ required: true, message: 'Please input the title of the page!' }]}
                >
                  <Input rootClassName={'grey'} />
                </Form.Item>
                <Divider/>
                <Form.Item
                  className={'border-sm round-2 p-3'}
                  name="description"
                  label={<Typography.Title level={5}>IQ Description</Typography.Title>}
                  rules={[{ required: false, message: 'Briefly describe your IQ for anyone who accesses it' }]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Enter a description for the IQ..."
                    defaultValue={initialValues?.description}
                  />
                </Form.Item>
                <Divider/>
                <Form.Item
                  name="is_public"
                  label={<Typography.Title level={5}>IQ Visibility</Typography.Title>}
                  valuePropName="checked"
                >
                  <Switch
                      checkedChildren={'Public IQ'}
                      unCheckedChildren={'Private IQ'}
                      defaultChecked={initialValues?.is_public || userDefaults?.is_public || false}
                  />
                </Form.Item>
                <Form.Item
                    className={'border-sm round-2 p-3'}
                    name="touchpoint_ids"
                    label={<Typography.Title level={5}>Touchpoints</Typography.Title>}
                    rules={[{ required: false, message: 'Please select touchpoints!' }]}
                >
                    <UserDataSelector selectedIds={selectedTouchpoints}
                                      onSelect={(selectedIds)=>setSelectedTouchpoints(selectedIds)}
                                      dataField={'touchpoint_details'} />
                </Form.Item>
                <Form.Item>
                <AppSelector onUpdate={(newAppList)=>setApps(newAppList)} allAvailableApps={allApps} selectedApps={apps} allowedApps={ALLOWED_IQ_APPS} />
                </Form.Item>
            </Col>
            <Col xs={20} xl={12} offset={1}>
                <Form.Item
                    className={'border-sm round-2 p-3'}
                  name="emails"
                  label={<Typography.Title level={5}>Emails Added to this IQ</Typography.Title>}
                >
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                    onChange={(values) => setEmails(values)}
                  />
                </Form.Item>
                <Divider />
                <Form.Item
                    className={'border-sm round-2 p-3'}
                    name="portfolio_ids"
                    label={<Typography.Title level={5}>Portfolios Included in Page</Typography.Title>}
                    rules={[{ required: false, message: 'Please select portfolio IDs!' }]}
                >
                    <PortfolioSelectorV2
                        onSelectionChange={(selectedIds)=>setSelectedPortfolios(selectedIds)}
                        selectedIds={selectedPortfolios.map(portfolio => portfolio.id)}
                    />
                </Form.Item>
                <Divider/>
                <Form.Item
                    className={'border-sm round-2 p-3'}

                    label={
                      <Button variant={'outlined'} startIcon={<PostAddRounded/>} onClick={() => setDrawerVisible(true)}>
                        Selected Files ({selectedFiles.length})
                      </Button>
                }>
                  {selectedFiles.map(file => (
                    <Tag
                      key={file}
                      closable
                      onClose={() => handleRemoveFile(file)}
                      style={{ marginBottom: 8, background: file === brand ? 'red': '#fff' }}
                      onClick={()=>setBrand(file)}
                    >
                      {file}
                    </Tag>
                  ))}
                </Form.Item>
            </Col>
        </Row>


      </Form>

      <Drawer
          open={drawerVisible}
          onClose={()=>setDrawerVisible(false)}
          width={'100%'}
          title={"Viewing Files as Popup"}
      >
        <Files onSelect={handleSelectedFiles} fullscreen={false} />
      </Drawer>
    </>
  );
};

export default PortfolioPageForm;
